import Nav from "../components/Nav";
import { Printer } from "lucide-react";

const CHAPTERS = [
    { n: "01", t: "Audit", d: "Forensic review of all three reports." },
    { n: "02", t: "Dispute", d: "Custom letters filed under FCRA §611." },
    { n: "03", t: "Remove", d: "Errors, collections & charge-offs deleted." },
    { n: "04", t: "Rebuild", d: "Strategic rebuild plan for lasting scores." },
];

export default function Flyer() {
    return (
        <div className="min-h-screen bg-secondary">
            <div className="no-print">
                <Nav />
            </div>

            <div className="pt-24 pb-16 no-print">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-2">
                            Print-ready flyer
                        </div>
                        <h1 className="font-editorial text-4xl md:text-5xl tracking-tighter">
                            Letter format · <span className="italic text-accent">8.5 × 11 in</span>
                        </h1>
                    </div>
                    <button
                        onClick={() => window.print()}
                        data-testid="flyer-print-btn"
                        className="inline-flex items-center gap-3 bg-foreground text-background px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-accent"
                    >
                        <Printer size={16} /> Print / Save as PDF
                    </button>
                </div>
            </div>

            {/* Flyer page */}
            <div className="mx-auto flex justify-center pb-24">
                <div
                    className="flyer-page bg-background text-foreground shadow-2xl relative"
                    style={{ width: "8.5in", minHeight: "11in", padding: "0.6in" }}
                    data-testid="flyer-page"
                >
                    {/* Header */}
                    <div className="flex items-start justify-between border-b hairline pb-6">
                        <div className="flex items-center gap-3">
                            <span className="w-6 h-6 border-2 border-foreground grid place-items-center">
                                <span className="w-2 h-2 bg-foreground" />
                            </span>
                            <div>
                                <div className="font-editorial text-2xl tracking-tight leading-none">
                                    Access <span className="italic text-accent">/</span> Credit Repair
                                </div>
                                <div className="mt-1 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                                    Austin, Texas · Est. 2022
                                </div>
                            </div>
                        </div>
                        <div className="text-right font-mono-ui text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                            Vol. I · Ed. 01
                        </div>
                    </div>

                    {/* Hero */}
                    <div className="mt-8">
                        <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                            A boutique credit restoration studio
                        </div>
                        <h1 className="mt-4 font-editorial text-[72px] leading-[0.86] tracking-tighter">
                            Your credit,
                            <br />
                            <span className="italic text-accent">rebuilt</span> with
                            <br />
                            quiet precision.
                        </h1>
                    </div>

                    {/* Chapters grid */}
                    <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5">
                        {CHAPTERS.map((c) => (
                            <div key={c.n} className="border-t-2 border-foreground pt-3">
                                <div className="flex items-baseline justify-between">
                                    <div className="font-mono-ui text-[10px] tracking-[0.2em] text-muted-foreground">
                                        Ch. {c.n}
                                    </div>
                                </div>
                                <div className="mt-1 font-editorial text-2xl tracking-tight">{c.t}</div>
                                <div className="mt-1 text-[11px] text-muted-foreground leading-snug">
                                    {c.d}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Guarantee callout */}
                    <div className="mt-10 border-2 border-accent p-5 flex items-center justify-between gap-6">
                        <div>
                            <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-accent">
                                The Access Guarantee
                            </div>
                            <div className="mt-2 font-editorial text-[26px] tracking-tight leading-tight">
                                A <span className="italic">93-point</span> increase — in writing.
                            </div>
                            <div className="mt-2 text-[11px] text-muted-foreground leading-snug max-w-md">
                                Enroll and follow our program. If your score doesn&rsquo;t climb by at
                                least 93 points, we keep working at no additional cost until it does.
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-editorial text-[56px] leading-none tracking-tighter text-accent">
                                +93
                            </div>
                            <div className="mt-1 font-mono-ui text-[9px] tracking-[0.18em] uppercase text-muted-foreground">
                                pts · guaranteed
                            </div>
                        </div>
                    </div>

                    {/* Stats band */}
                    <div className="mt-8 bg-foreground text-background p-5 grid grid-cols-4 gap-3">
                        {[
                            ["+178", "avg. lift"],
                            ["5 days", "1st dispute"],
                            ["94%", "success rate"],
                            ["2,300+", "items removed"],
                        ].map(([k, v]) => (
                            <div key={k}>
                                <div className="font-editorial text-3xl tracking-tighter">{k}</div>
                                <div className="mt-1 font-mono-ui text-[9px] tracking-[0.18em] uppercase text-background/70">
                                    {v}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Services list */}
                    <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-1 text-[12px]">
                        {[
                            "Credit report audit",
                            "Dispute filing (FCRA §611)",
                            "Score improvement strategy",
                            "Debt negotiation & settlement",
                            "Collections removal (FDCPA)",
                            "Identity theft resolution",
                        ].map((s) => (
                            <div key={s} className="flex items-center gap-3 border-b hairline py-2">
                                <span className="w-1.5 h-1.5 bg-accent" />
                                <span>{s}</span>
                            </div>
                        ))}
                    </div>

                    {/* Footer / CTA — spaced from the block above via mt-auto */}
                    <div className="mt-auto pt-8">
                        <div className="border-t-2 border-foreground pt-5 grid grid-cols-12 gap-4 items-end">
                            <div className="col-span-7">
                                <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                                    Book a free 20-minute consultation
                                </div>
                                <div className="mt-2 font-editorial text-[38px] leading-none tracking-tighter">
                                    512.783.1895
                                </div>
                                <div className="mt-2 font-mono-ui text-xs text-muted-foreground break-all">
                                    accesscreditrepair@yahoo.com
                                </div>
                            </div>
                            <div className="col-span-5 text-right">
                                <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-muted-foreground">
                                    CROA compliant. No upfront fees. Written contract. 3-day right to cancel.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
